import { useEffect } from 'react'

/**
 * HeroVideoPause - stops an ambient hero loop decoding while it is off screen.
 *
 * Covers both elements that carry .hero-video: the home hero's full-bleed plate
 * (/assets/hero-loop.mp4, Pentabay.html) and the topicPage banner plate that
 * routes opt into with cfg.heroVideo (/videos/hero-laptop.mp4 on /design,
 * public/pentabay-subpages.js). The class is shared on purpose, so one observer
 * covers both and any route that opts in later.
 *
 * ── What already existed, and what did not ────────────────────────────────
 * ROUTE level was already handled: teardownVideos()/restoreVideos() in
 * Pentabay.html pause + display:none every <video> when a page is parked, and
 * explicitly play() on the way back, because Chrome pauses a media element when
 * its subtree leaves the document and `autoplay` does not fire a second time on
 * re-attach.
 *
 * VIEWPORT level was not handled at all. On the home page the hero is one
 * screen of a long document, so scrolling to the footer left a 1080p loop
 * decoding for the rest of the session. That is what this fixes; it is the only
 * gap, which is why nothing else here changes.
 *
 * ── IntersectionObserver, not ScrollTrigger ───────────────────────────────
 * ScrollTrigger resolves start/end to scroll offsets once and only re-evaluates
 * on scroll or refresh, so it has no idea its trigger was detached - and this
 * SPA parks routes by detaching them (PB_parkPage does node.remove() and stashes
 * the node for reuse). That combination was measured going wrong in
 * CtaReveal.jsx: a detached element's ScrollTrigger stayed "active" and its
 * tween kept running at the full frame rate. IO is driven by layout instead, so
 * a detached or display:none video is simply not intersecting.
 *
 * That last part is also why reduced motion needs no branch here. Both plates
 * are display:none under `reduce` (StyleOverrides: [data-route="home"]
 * .hero-video, and .hero-shell.pb-hero-banner .hero-video-plate), so IO reports
 * them non-intersecting and they stay paused - the reduced-motion contract is
 * enforced in CSS and this just follows it. There is no animation here to skip.
 *
 * ── Why it only resumes what it paused ────────────────────────────────────
 * A paused hero video is not always ours to restart. autoplay+muted can be
 * refused outright (low-power mode, data saver), in which case the poster is the
 * documented fallback and forcing play() would fight the user's setting; and a
 * parked page's video is paused by teardownVideos, which owns restarting it.
 * So a video is only played again if this file is the thing that paused it,
 * tracked with data-pb-vid-offscreen.
 */

const SEL = '.hero-video'
const FLAG = 'pbVidOffscreen'   // -> data-pb-vid-offscreen

export default function HeroVideoPause() {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return

    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const v = e.target
        if (e.isIntersecting) {
          // Only resume our own pause. See the note above.
          if (v.dataset[FLAG] === '1') {
            delete v.dataset[FLAG]
            if (v.paused) {
              const p = v.play()
              if (p && p.catch) p.catch(() => {})
            }
          }
        } else if (!v.paused) {
          v.dataset[FLAG] = '1'
          v.pause()
        }
      }
    })

    /* Pages mount lazily and a parked page re-attaches the same node, so the
       set of .hero-video elements grows over a session. observe() is idempotent
       per element in the spec, but the WeakSet keeps the scan cheap and makes
       the intent explicit. */
    const seen = new WeakSet()
    const scan = () => {
      for (const v of document.querySelectorAll(SEL)) {
        if (seen.has(v)) continue
        seen.add(v)
        io.observe(v)
      }
    }

    scan()
    const mo = new MutationObserver(scan)
    mo.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('pb-pages-mounted', scan)

    return () => {
      io.disconnect()
      mo.disconnect()
      window.removeEventListener('pb-pages-mounted', scan)
      /* Leave nothing half-paused behind: anything this file stopped gets its
         flag cleared and a play() attempt, so unmounting cannot strand a hero
         on a still frame. */
      for (const v of document.querySelectorAll(SEL)) {
        if (v.dataset[FLAG] === '1') {
          delete v.dataset[FLAG]
          if (v.paused) {
            const p = v.play()
            if (p && p.catch) p.catch(() => {})
          }
        }
      }
    }
  }, [])

  return null
}
